import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnackBar } from "@/utils/useSnackbar";
import { useAppDispatch } from "@/redux/hooks";
import { clearGlobalSearch } from "@/pages/Private/redux/search/search.slice";
import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";
import { useAddPersonToListMutation } from "@/pages/Private/redux/list/list.api";
import { useLazyGetCompanyEnrichPersonQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";
import { useProgress } from "@/pages/Private/pages/Messages/utils/useProgress";

import { SelectedLinkedInItem } from "./singleSelect";
import { resolveContactLinkedinUrl, resolveLinkedInItemUrl } from "./resolveSelectionLinkedinUrl";

// Request body for the Compose-list add (POST v1/list/person) in single-select "Write message" mode.
type ComposeAddBody = {
	method: "linkedinUrl" | "nameCompany";
	linkedinUrl?: string;
	name?: string;
	companyName?: string;
};

interface UseComposeWriteMessageArgs {
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
	// The global-search slice's contacts bucket — used to look up a selected contact's linkedinUrl.
	searchContacts: ListPersonGlobalSearchInfoDTO[];
	isEnrichAllEmails: boolean;
	isEnrichAllPhones: boolean;
	onPersonAdded?: (newLeadId?: number) => void;
	setIsOpened: Dispatch<SetStateAction<boolean>>;
}

interface UseComposeWriteMessageResult {
	// Drives the progress overlay's visibility through the add + a short "done" hold.
	isWriting: boolean;
	progress: number;
	currentFunnyText: string;
	handleWriteMessage: () => Promise<void>;
}

/**
 * Encapsulates the single-select "Write message" (compose) flow of GlobalSearchModal: resolving the
 * one selected person into an add payload, adding them to the company's Compose list, and driving
 * the progress overlay + delayed close. Kept out of the modal so that component stays focused on
 * search UI + selection state.
 */
export function useComposeWriteMessage({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	searchContacts,
	isEnrichAllEmails,
	isEnrichAllPhones,
	onPersonAdded,
	setIsOpened,
}: UseComposeWriteMessageArgs): UseComposeWriteMessageResult {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { handleOpenSnackbar } = useSnackBar();

	// Compose adds land in the company's Compose list (COMPOSE_LIST) — same endpoint the legacy
	// ComposeModal used.
	const [addPersonToComposeList, { isLoading: isComposing }] = useAddPersonToListMutation();
	// Resolves a companyEnrich person → their LinkedIn URL so compose can use the reliable
	// linkedinUrl path (the same one that works for contacts) instead of fuzzy name+company.
	const [resolveCompanyEnrichPerson] = useLazyGetCompanyEnrichPersonQuery();
	// Resolves a contact's linkedinUrl by id when it isn't in the search slice (e.g. By URL tab).
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();

	// Animated progress + rotating status text shown while the compose add resolves/enriches.
	const { progress, currentFunnyText } = useProgress(isComposing);
	// Drives the progress overlay's visibility. Stays true through completion (the bar hits 100%
	// when `isComposing` flips false) and a short hold, so the modal closes only AFTER the bar is
	// done. Decoupled from `isComposing` so it doesn't vanish the instant the request resolves.
	const [isWriting, setIsWriting] = useState(false);
	// Holds the "finish progress, then close" timer so we can cancel it if the modal unmounts before
	// it fires — avoids setting state on an unmounted component.
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		},
		[]
	);

	// Resolve the one selected person into the Compose-add payload. Prefers the reliable linkedinUrl
	// path: existing contacts use their stored URL (from the search slice, or fetched from the
	// contact detail when selected via the By URL tab — the slice is empty there); companyEnrich
	// people are resolved to a URL via their person detail; URL-resolved prospects already carry one.
	// Falls back to name + company only when no URL is resolvable.
	const resolveComposeAddBody = useCallback(async (): Promise<ComposeAddBody | undefined> => {
		const contactId = selectedContactIds[0];

		if (contactId !== undefined) {
			const linkedinUrl = await resolveContactLinkedinUrl(contactId, searchContacts, (id) =>
				triggerGetContactProspectDetail(id)
					.unwrap()
					.catch(() => undefined)
			);

			return linkedinUrl ? { method: "linkedinUrl", linkedinUrl } : undefined;
		}

		const li = selectedLinkedInItems[0];

		if (!li) {
			return undefined;
		}

		const resolvedUrl = await resolveLinkedInItemUrl(li, (id) =>
			resolveCompanyEnrichPerson(id)
				.unwrap()
				.catch(() => undefined)
		);

		if (resolvedUrl) {
			return { method: "linkedinUrl", linkedinUrl: resolvedUrl };
		}

		return li.name ? { method: "nameCompany", name: li.name, companyName: li.domain } : undefined;
	}, [
		selectedContactIds,
		selectedLinkedInItems,
		searchContacts,
		triggerGetContactProspectDetail,
		resolveCompanyEnrichPerson,
	]);

	// Add the one selected person to the Compose list, then close and land in their chat (preserving
	// the legacy ComposeModal flow). Orchestrates the lifecycle — payload building is delegated to
	// resolveComposeAddBody.
	const handleWriteMessage = useCallback(async () => {
		const body = await resolveComposeAddBody();

		if (!body) {
			handleOpenSnackbar(t("basics.error"));

			return;
		}

		// Show the progress overlay and keep the modal open while the add resolves/enriches.
		setIsWriting(true);

		try {
			const result = await addPersonToComposeList({
				...body,
				enrichEmail: isEnrichAllEmails,
				enrichPhone: isEnrichAllPhones,
			}).unwrap();

			handleOpenSnackbar(t("contacts.addedToList", { name: "Compose" }));
			// Hand the new lead's id to the caller so it can show + select that exact lead.
			onPersonAdded?.(result?.data?.[0]?.id);
			dispatch(clearGlobalSearch());
			setSelectedContactIds([]);
			setSelectedLinkedInItems([]);

			// Hold the completed (100%) progress bar briefly, then close + navigate to Compose —
			// mirrors the legacy ComposeModal "finish, then redirect" feel. ~500ms matches the
			// useProgress hold-at-100% window so the bar reads "done" before the modal closes.
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}

			closeTimeoutRef.current = setTimeout(() => {
				setIsWriting(false);
				setIsOpened(false);
			}, 500);
		} catch {
			setIsWriting(false);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [
		resolveComposeAddBody,
		isEnrichAllEmails,
		isEnrichAllPhones,
		addPersonToComposeList,
		handleOpenSnackbar,
		t,
		dispatch,
		setIsOpened,
		onPersonAdded,
		setSelectedContactIds,
		setSelectedLinkedInItems,
	]);

	return { isWriting, progress, currentFunnyText, handleWriteMessage };
}
